import { create } from 'zustand';

/**
 * App-wide signals raised by the API layer and read by the shell:
 * - `gateReason`: the server answered 428 INSTALL_REQUIRED, so the shell renders the gate.
 * - `signedOutAt`: a member route answered 401, so the shell drops to Welcome.
 * - `updateRequired`: 426 CLIENT_OUTDATED or a config minClient above us.
 * - `serverVersion`: the last X-App-Version header seen.
 * - `clockOffsetMs`: serverNow minus Date.now(), smoothed. Event clientTs uses it.
 */
interface Signals {
  gateReason: 'server' | null;
  signedOutAt: number | null;
  updateRequired: boolean;
  serverVersion: string | null;
  clockOffsetMs: number;
  forceGate(): void;
  signedOut(): void;
  clearSignedOut(): void;
  requireUpdate(): void;
  sawServerVersion(v: string): void;
  sawServerNow(serverNow: number): void;
}

export const useSignals = create<Signals>((set, get) => ({
  gateReason: null,
  signedOutAt: null,
  updateRequired: false,
  serverVersion: null,
  clockOffsetMs: 0,
  forceGate: () => set({ gateReason: 'server' }),
  signedOut: () => set({ signedOutAt: Date.now() }),
  clearSignedOut: () => set({ signedOutAt: null }),
  requireUpdate: () => set({ updateRequired: true }),
  sawServerVersion: (v) => {
    if (get().serverVersion !== v) set({ serverVersion: v });
  },
  sawServerNow: (serverNow) => {
    const sample = serverNow - Date.now();
    const prev = get().clockOffsetMs;
    // EWMA so one slow response does not yank the clock around.
    set({ clockOffsetMs: prev === 0 ? sample : Math.round(prev * 0.8 + sample * 0.2) });
  },
}));

/** Date.now() corrected by the server clock offset. Use it for event clientTs. */
export function serverNow(): number {
  return Date.now() + useSignals.getState().clockOffsetMs;
}
